import { createEffect, createSignal, useContext } from 'solid-js';
import { AppContext } from '../../../../app-context-provider/app-context-provider';
import { Button } from '../../../../ui-components/button/button';
import { StyledTextInput } from '../../../../ui-components/inputs/text-input';
import { Modal } from '../../../../ui-components/modal/modal';
import { pdfStore, setPdfStore } from '../../pdf-store';
import { Text } from '../../../../ui-components/text/text';
import { StyledModalInner, StyledErrorMessage, StyledModalButtonsContainer, StyledWarningMessage } from './title-modal.style';

export const TitleModal = (props: {
    createNewCollection: () => void;
    saveExistingCollection: () => void;
}) => {

    const { localize } = useContext(AppContext);
    let input: any;
    
    const [ filenameError, setFilenameError ] = createSignal('');
    const [ filenameWarning, setFilenameWarning ] = createSignal('');
    
    createEffect(() => {
        if (pdfStore.status === 'naming') {
            input?.focus();
        }
    });

    const updateTitle = (e: Event) => {
        if (filenameError()) {
            setFilenameError('');
        }

        const target = e.target as HTMLInputElement;
        if (!target.value) {
            return;
        }

        const ok = validateTitle(target.value);
        if (!ok) {
            return;
        }

        setPdfStore('title',target.value);
    };

    const validateTitle = (title: string) => {
        const regex = /^[-a-zA-Z0-9_ ]*$/;
        const ok = regex.test(title);

        if (!ok) {
            setFilenameError(localize('pdf-title-allowed-characters', 'Please only use alphanumeric characters, dashes, underscores and spaces'));
            return false;
        }

        return true;
    };

    const saveBuild = async () => {
        if (duplicateTitle()) {
            props.saveExistingCollection();
            return;
        };

        setPdfStore('status', 'creating');
        props.createNewCollection();
    };

    const duplicateTitle = () => {
        setFilenameWarning('');
        setFilenameError('');

        const existingTitles = pdfStore.collections.map((collection) => collection.title);
        const isDuplicate = existingTitles.includes(pdfStore.title);

        if (isDuplicate) {
            setFilenameWarning(localize('pdf-same-name-warning', 'A collection with this name already exists. Do you want to overwrite it?'));
            return true;
        }

        return isDuplicate;
    };

    const closeModal = () => {
        setPdfStore('status', '');
    };

    return (
        <Modal visible={ pdfStore.status === 'naming' } onClose={ closeModal } size='small' darkenBackground={ true }>
            <StyledModalInner>
                <Text fontSize='large' fontStyle='bold'>
                    {localize('pdf-name-of-file', 'Name your file')}
                </Text>
                <StyledTextInput
                    ref={input}
                    value={ pdfStore.title }
                    onInput={ ((e) => updateTitle(e)) }
                    placeholder={ localize('name', 'Name') }
                />
                <StyledErrorMessage>{filenameError()}</StyledErrorMessage>
                <StyledWarningMessage>{filenameWarning()}</StyledWarningMessage>
                <StyledModalButtonsContainer>
                    <Button
                        label={ localize('cancel', 'Cancel') }
                        variant='secondary'
                        onClick={ closeModal }
                    />
                    <Button
                        disabled={ !pdfStore.title }
                        label={ duplicateTitle() 
                            ? localize('pdf-overwrite-existing', 'Overwrite')
                            : localize('pdf-builder-create', 'Create')
                        }
                        onClick={ saveBuild }
                    />
                </StyledModalButtonsContainer>
            </StyledModalInner>
        </Modal>
    );
};
